import type { Config, Variant } from '@dashboard/types/config'
import { renderText } from '@shared/services/text'
import { segmentName } from '@shared/services/segmentName'
import { experienceLabel } from '@dashboard/services/string'
import { audienceTerms } from './campaignEdit'

export type GroupCatalogEntry = {
  id: string
  name: string
  kind: 'question' | 'segment-group'
  multiWinning: boolean
  segments: { id: string; name: string; originalName?: string }[]
}

export function buildGroupCatalog(
  config: Pick<Config, 'dimensions' | 'rightAsk'> & Partial<Pick<Config, 'phrases'>>
): GroupCatalogEntry[] {
  const questions = config.rightAsk?.questions ?? []
  const nameOptions = {
    segmentGroups: config.dimensions,
    questions: questions.map((question) => ({
      segmentGroupId: question.dimensionId,
      otherOptions: question.otherOptions
    }))
  }
  return [...(config.dimensions ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((group): GroupCatalogEntry => {
      const question = questions.find((item) => item.dimensionId === group.id)
      const textOptions = { phrases: config.phrases, truncationLimit: Number.POSITIVE_INFINITY }
      return {
        id: group.id,
        name: renderText(group.name, textOptions),
        kind:
          question && (question.isQuestion || renderText(question.text, textOptions).trim())
            ? 'question'
            : 'segment-group',
        multiWinning: group.isMultiWinning,
        segments: group.segments.map((segment) => ({
          id: segment.id,
          name: renderText(segmentName(segment, nameOptions) ?? '', textOptions),
          ...(segment.name === 'INT_OTHER' ? { originalName: segment.name } : {})
        }))
      }
    })
}

export function nameNewAudiences<T extends { name: string; groupName: string }>(
  audiences: readonly T[],
  existingNames: readonly string[] = []
): T[] {
  const normalized = audiences.map((audience) => {
    const name = experienceLabel(audience.name)
    return name === audience.name ? audience : { ...audience, name }
  })
  const duplicated = new Set<string>()
  const reserved = new Set(existingNames.map(experienceLabel))
  for (const { name } of normalized) {
    if (reserved.has(name)) {
      duplicated.add(name)
    }
    reserved.add(name)
  }
  return normalized.map((audience) => {
    if (!duplicated.has(audience.name)) {
      return audience
    }
    const base = `${experienceLabel(audience.groupName)}: ${audience.name}`
    let name = base
    let suffix = 2
    while (reserved.has(name)) {
      name = `${base} (${suffix++})`
    }
    reserved.add(name)
    return { ...audience, name }
  })
}

export function audienceDisplayName(audience: Variant, groups: readonly GroupCatalogEntry[]): string {
  const name = experienceLabel(audience.name)
  if (name && !name.includes('INT_OTHER')) {
    return name
  }
  const targeting = audienceTerms(audience)
  const segments = targeting.terms.map((term) =>
    groups.find((group) => group.id === term.groupId)?.segments.find((segment) => segment.id === term.segmentId)
  )
  if (!name) {
    return (
      segments
        .map((segment) => segment?.name ?? 'Deleted segment')
        .join(targeting.operator === 'any' ? ' or ' : ' and ') || 'Untargeted audience'
    )
  }
  const otherSegments = segments.filter((segment) => segment?.originalName === 'INT_OTHER')
  let index = 0
  return name.replace(/\bINT_OTHER\b/g, () => {
    const segment = otherSegments[index++] ?? otherSegments.at(-1)
    return segment?.name ?? segmentName({ id: audience.id, name: 'INT_OTHER' }) ?? ''
  })
}
